import {
  GiftCouponShareStatus,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';

// === 쿠폰 공유 상태 ===

const COUPON_SHARE_STATUS_STYLE: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: 'bg-gray-100 text-gray-600',
  [GiftCouponShareStatus.Shared]: 'bg-green-100 text-green-700',
  [GiftCouponShareStatus.Viewed]: 'bg-blue-100 text-blue-700',
};

const COUPON_SHARE_STATUS_LABEL: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: '미전달',
  [GiftCouponShareStatus.Shared]: '전달완료',
  [GiftCouponShareStatus.Viewed]: '확인됨',
};

export const getCouponShareStatusStyle = (status: GiftCouponShareStatus) =>
  COUPON_SHARE_STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600';

export const getCouponShareStatusLabel = (status: GiftCouponShareStatus) =>
  COUPON_SHARE_STATUS_LABEL[status] ?? status;

// === 배송 상태 ===

const DELIVERY_STATUS_STYLE: Record<GiftDeliveryStatus, string> = {
  [GiftDeliveryStatus.WaitingAddress]: 'bg-amber-100 text-amber-700',
  [GiftDeliveryStatus.AddressSubmitted]: 'bg-blue-100 text-blue-700',
  [GiftDeliveryStatus.Preparing]: 'bg-indigo-100 text-indigo-700',
  [GiftDeliveryStatus.Shipping]: 'bg-purple-100 text-purple-700',
  [GiftDeliveryStatus.Delivered]: 'bg-green-100 text-green-700',
  [GiftDeliveryStatus.Canceled]: 'bg-red-100 text-red-700',
};

const DELIVERY_STATUS_LABEL: Record<GiftDeliveryStatus, string> = {
  [GiftDeliveryStatus.WaitingAddress]: '배송지 입력 대기',
  [GiftDeliveryStatus.AddressSubmitted]: '배송지 입력 완료',
  [GiftDeliveryStatus.Preparing]: '상품 준비중',
  [GiftDeliveryStatus.Shipping]: '배송중',
  [GiftDeliveryStatus.Delivered]: '배송 완료',
  [GiftDeliveryStatus.Canceled]: '취소',
};

export const getDeliveryStatusStyle = (status: GiftDeliveryStatus) =>
  DELIVERY_STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600';

export const getDeliveryStatusLabel = (status: GiftDeliveryStatus) =>
  DELIVERY_STATUS_LABEL[status] ?? status;

// === 배송 공유 상태 ===

const DELIVERY_SHARE_STATUS_STYLE: Record<GiftDeliveryShareStatus, string> = {
  [GiftDeliveryShareStatus.NotShared]: 'bg-gray-100 text-gray-600',
  [GiftDeliveryShareStatus.Shared]: 'bg-green-100 text-green-700',
  [GiftDeliveryShareStatus.Viewed]: 'bg-blue-100 text-blue-700',
};

const DELIVERY_SHARE_STATUS_LABEL: Record<GiftDeliveryShareStatus, string> = {
  [GiftDeliveryShareStatus.NotShared]: '미전달',
  [GiftDeliveryShareStatus.Shared]: '전달완료',
  [GiftDeliveryShareStatus.Viewed]: '확인됨',
};

export const getDeliveryShareStatusStyle = (status: GiftDeliveryShareStatus) =>
  DELIVERY_SHARE_STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600';

export const getDeliveryShareStatusLabel = (status: GiftDeliveryShareStatus) =>
  DELIVERY_SHARE_STATUS_LABEL[status] ?? status;

export const SHARE_GUIDE_DISMISSED_KEY = 'gift-coupon-share-guide-dismissed';

// === Pending Confirm (전달 확인 대기) 상태 localStorage 관리 ===

const PENDING_CONFIRM_KEY = 'gift-pending-confirm';

/**
 * 스키마 버전.
 * - v1: channel 이 UI 전용 소문자 union (`'sms' | 'kakao' | 'other'`) — 더 이상 사용 안 함
 * - v2: channel 이 백엔드 ShareChannel enum (`'KakaoTalk' | 'SMS' | 'Link' | 'Other'`)
 *
 * 구버전 엔트리는 매칭 로직에서 단순 무시하고 자연스럽게 만료시킨다 (영향 범위 작음).
 */
const PENDING_CONFIRM_VERSION = 2;

export interface PendingConfirmEntry {
  v: typeof PENDING_CONFIRM_VERSION;
  type: 'coupon' | 'delivery';
  itemId: string;
  seq: number;
  channel: ShareChannel;
  orderId: string;
  timestamp: number;
}

type PendingConfirmEntryInput = Omit<PendingConfirmEntry, 'v'>;

export function savePendingConfirm(entry: PendingConfirmEntryInput): void {
  try {
    const existing = loadPendingConfirms();
    const filtered = existing.filter((e) => e.itemId !== entry.itemId);
    filtered.push({ ...entry, v: PENDING_CONFIRM_VERSION });
    localStorage.setItem(PENDING_CONFIRM_KEY, JSON.stringify(filtered));
  } catch {
    // localStorage 접근 실패 무시
  }
}

export function loadPendingConfirms(): PendingConfirmEntry[] {
  try {
    const raw = localStorage.getItem(PENDING_CONFIRM_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // 구버전 엔트리(v 가 없거나 다름)는 무시 — channel enum 변경으로 호환 불가
    return parsed.filter(
      (e): e is PendingConfirmEntry =>
        e && typeof e === 'object' && e.v === PENDING_CONFIRM_VERSION,
    );
  } catch {
    return [];
  }
}

export function loadPendingConfirmForOrder(orderId: string): PendingConfirmEntry | null {
  const all = loadPendingConfirms();
  return all.find((e) => e.orderId === orderId) ?? null;
}

export function removePendingConfirm(itemId: string): void {
  try {
    const existing = loadPendingConfirms();
    const filtered = existing.filter((e) => e.itemId !== itemId);
    localStorage.setItem(PENDING_CONFIRM_KEY, JSON.stringify(filtered));
  } catch {
    // localStorage 접근 실패 무시
  }
}
